import { useState } from "react";
import {
  DollarSign,
  Wrench,
  AlertTriangle,
  Phone,
  Search,
  User,
  Mail,
  MapPin,
  Eye,
  Download,
  Banknote,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import { DashboardHeader } from "../header/header";
import { Dashboard } from "../dashboard/dashboard";
import { RecentInteractions } from "../consumerRecentInteractionsList/consumerRecentInteractionsList";
import { QuickActions } from "../quickAction/quickAction";
import { ConsumerSelectionDialog } from "../consumerSelectionAdd/consumerSelectionAdd";
import { useNavigate } from "react-router-dom";
import {
  useAllCallEntries,
  useConsumerSearchWithExtraInfo,
  useDownloadBill,
  useGetConsumerBill,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { formatTerritoryAddress } from "@shared/utils/address";

interface Consumer {
  id: string;
  consumerNo: string;
  name: string;
  accountNumber: string;
  serviceAddress: string;
  phone: string;
  email: string;
  status: string;
  category: string;
  subCategory: string;
  plan: string;
  currentBalance: string;
  lastPaymentDate: string;
  lastPayment: string;
}

export default function CallCenterLanding() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [selectedConsumer, setSelectedConsumer] = useState<any>(null);
  const [billID, setBillID] = useState<any>(null);
  const { data: allCallEntries } = useAllCallEntries();
  const { data: searchConsumerBill } = useConsumerSearchWithExtraInfo({
    remoteUtilityId: getRemoteUtilityId(),
    searchData: searchTerm,
    isExtraInfo: 1,
  });

  const { data: consumerBill } = useGetConsumerBill({
    remoteUtilityId: getRemoteUtilityId(),
    consumerNumber: selectedConsumer?.consumerNo,
    addressRequired: true,
    fetchLatest: true,
    isPlanRequired: 1,
  });
  console.log("Selected Consumer Bill Data:", consumerBill);
  const serviceAddress =
    formatTerritoryAddress({ service: consumerBill?.result?.addressData }, "service") ||
    "No address configured";
  const downloadBill = useDownloadBill(billID);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-success/10 text-success border-success/20";
      case "overdue":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "inactive":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const handleProcessPayment = (consumer: Consumer) => {
    navigate(`/call-center/payment/create/${consumer.id}`);
  };

  const handleRequestService = (consumer: Consumer) => {
    navigate(
      `/call-center/services/create/${consumer.id}/${consumer.consumerNo}`
    );
  };

  const handleRaiseComplaint = (consumer: Consumer) => {
    navigate(
      `/call-center/complaints/create/${consumer.id}/${consumer.consumerNo}`
    );
  };

  const handleDownloadBill = async (billID: any) => {
    try {
      await downloadBill.mutateAsync({
        billId: billID,
        filename: `bill-${billID}.pdf`,
      });
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleConsumerSelect = (consumer: any) => {
    setSelectedConsumer(consumer);
  };

  const quickActions = [
    {
      title: "Call Consumer",
      description: "Start a new call with a customer",
      icon: Phone,
      action: () => setIsCallDialogOpen(true),
    },
    {
      title: "Process Payment",
      description: "Record and process customer payments",
      icon: Banknote,
      action: () => window.open("/cx/payments/create", "_blank"),
    },
    {
      title: "Create Service Request",
      description: "Register new service requests from customers",
      icon: Wrench,
      action: () => window.open("/cx/services/create", "_blank"),
    },
    {
      title: "Log Complaint",
      description: "Record and track customer complaints",
      icon: AlertTriangle,
      action: () => window.open("/cx/complaints/create", "_blank"),
    },
  ];

  const handleCallStarted = () => {
    console.log("Call started from dashboard");
  };

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Integrated ConsumerDetails rendering function
  const renderConsumerDetails = (consumer: any) => {
    console.log("Rendering details for consumer:", consumer);
    return (
      <Card
        key={consumerBill?.result?.id}
        className="hover:shadow-md transition-shadow"
      >
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Header - Consumer Identity */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {consumerBill?.result?.firstName}{" "}
                    {consumerBill?.result?.lastName}
                  </h3>
                  <p className="text-xs text-muted-foreground font-mono">
                    {consumerBill?.result?.consumerNo}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  className={getStatusColor(
                    consumerBill?.result?.statusDisplay
                  )}
                  variant="outline"
                >
                  {consumerBill?.result?.statusDisplay}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs px-2 py-1 h-7"
                  onClick={() =>
                    navigate(
                      `/cx/consumer-management/${consumerBill?.result?.consumerNo}`
                    )
                  }
                >
                  <Eye className="h-3 w-3 mr-1" />
                  view
                </Button>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-muted/30 rounded-md p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  <span className="text-foreground">
                    {consumerBill?.result?.contactNumber}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <span className="text-foreground truncate">
                    {consumerBill?.result?.email}
                  </span>
                </div>
                <div className="flex items-start gap-1">
                  <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="text-foreground text-xs leading-tight">
                    {serviceAddress}
                  </span>
                </div>
              </div>
            </div>

            {/* Account Details - Merged Service & Billing */}
            <div className="bg-muted/20 rounded-md p-3">
              <h4 className="text-xs font-medium text-foreground mb-2 flex items-center gap-1">
                <Wrench className="h-3 w-3" />
                Account Details
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Category:</span>
                  <span className="font-medium text-foreground ml-1">
                    {consumerBill?.result?.categoryDisplay || "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Sub Category:</span>
                  <span className="font-medium text-foreground ml-1">
                    {consumerBill?.result?.subCategoryDisplay || "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Plan:</span>
                  <span className="font-medium text-foreground ml-1">
                    {consumerBill?.result?.planDetails?.planName || "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Balance:</span>
                  <span
                    className={`font-semibold ml-1 ${
                      consumerBill?.result?.billData[0]?.outstandingBalance ===
                      "$0.00"
                        ? "text-success"
                        : "text-destructive"
                    }`}
                  >
                    {consumerBill?.result?.billData[0]?.outstandingBalance ||
                      "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Last Pay:</span>
                  <span className="font-medium text-foreground ml-1">
                    {consumerBill?.result?.billData[0]?.lastPayAmount || "N/A"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium text-foreground ml-1">
                    {consumerBill?.result?.billData[0]?.lastPaymentDate ||
                      "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="border-t pt-3">
              <div className="flex gap-1 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleProcessPayment(consumer)}
                  className="text-xs px-2 py-1 h-7"
                >
                  <DollarSign className="h-3 w-3 mr-1" />
                  Pay
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRequestService(consumer)}
                  className="text-xs px-2 py-1 h-7"
                >
                  <Wrench className="h-3 w-3 mr-1" />
                  Service
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRaiseComplaint(consumer)}
                  className="text-xs px-2 py-1 h-7"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Complaint
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!consumerBill?.result?.billData[0]?.id}
                  onClick={() =>
                    handleDownloadBill(consumerBill?.result?.billData[0]?.id)
                  }
                  className="text-xs px-2 py-1 h-7"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Bill
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Modified SearchInfo rendering function
  const renderSearchInfo = () => {
    // Show selected consumer details only if a consumer is selected AND search hasn't changed
    if (
      selectedConsumer &&
      searchConsumerBill?.results?.some(
        (result: any) => result.id === selectedConsumer.id
      )
    ) {
      return (
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Consumer Details
          </h2>
          {renderConsumerDetails(selectedConsumer)}
        </div>
      );
    }

    // Show search results as simple clickable list
    if (searchConsumerBill?.results?.length > 0) {
      return (
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Search Results ({searchConsumerBill?.results?.length || 0} found)
          </h2>

          {/* Scrollable container */}
          <div className="max-h-[300px] overflow-y-auto pr-2">
            <div className="grid gap-3">
              {searchConsumerBill?.results?.map((consumer: any) => (
                <Card
                  key={consumer.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleConsumerSelect(consumer)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {consumer.firstName} {consumer.lastName}
                          </h3>
                          <p className="text-xs text-muted-foreground font-mono">
                            {consumer.consumerNo}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={getStatusColor(consumer.statusDisplay)}
                          variant="outline"
                        >
                          {consumer.statusDisplay}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (searchTerm && searchConsumerBill?.results?.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center gap-2">
              <Search className="h-12 w-12 text-muted-foreground" />
              <p className="text-lg font-medium text-foreground">
                No consumers found
              </p>
              <p className="text-muted-foreground">
                Try searching with a different term
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <DashboardHeader currentDate={currentDate} />

      <Dashboard />

      {/* Integrated ConsumerSearch */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Search Consumers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mt-4">
            <Input
              placeholder="Search by consumer number, first name, last name, or status..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Clear selected consumer when search term is cleared or changed
                if (
                  e.target.value === "" ||
                  (selectedConsumer &&
                    !e.target.value.includes(selectedConsumer.consumerNo) &&
                    !e.target.value.includes(selectedConsumer.firstName) &&
                    !e.target.value.includes(selectedConsumer.lastName))
                ) {
                  setSelectedConsumer(null);
                }
              }}
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Integrated SearchInfo */}
      {renderSearchInfo()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentInteractions interactions={allCallEntries} />
        <QuickActions actions={quickActions} />
      </div>

      <ConsumerSelectionDialog
        open={isCallDialogOpen}
        onOpenChange={setIsCallDialogOpen}
        onCallStarted={handleCallStarted}
      />
    </div>
  );
}
